/**
 * Handoff from prerendered HTML to the live app.
 *
 * Hydrating a prerendered page always fails: the saved HTML shows loaded data while the
 * first client render shows loading states, so React discarded the page and showed
 * "Loading..." for seconds. Instead, index.tsx leaves the saved HTML on screen, renders
 * the app out of sight, and swaps it in once the page calls useContentReady(true).
 */

import { useEffect } from 'react';

const READY_EVENT = 'cni:content-ready';
/** Swap anyway if a page never reports ready (e.g. an unexpected empty view). */
const READY_TIMEOUT_MS = 15000;
/** The page body scrolls inside this element (see App in index.tsx); the swap carries its position over. */
const SCROLL_CONTAINER = '.main-content-wrapper';

let pending = false;
let ready = false;

/** True while the saved HTML is still on screen and the app renders out of sight. */
export const isHandoffPending = (): boolean => pending;

export function markContentReady(): void {
  if (ready) return;
  ready = true;
  window.dispatchEvent(new Event(READY_EVENT));
}

/**
 * Pages call this with true once their data-driven content is rendered. delayMs lets a
 * fade-in finish (transitions still run while hidden) so the swap never shows it midway.
 */
export function useContentReady(isReady: boolean, delayMs = 0): void {
  useEffect(() => {
    if (!isReady) return;
    if (!delayMs) {
      markContentReady();
      return;
    }
    const timer = window.setTimeout(markContentReady, delayMs);
    return () => window.clearTimeout(timer);
  }, [isReady, delayMs]);
}

/**
 * The saved HTML can be reused only if it was prerendered for this URL. URLs without a
 * prerendered file get build/app-shell.html (unmarked, empty #root), so they render fresh.
 */
export function prerenderedHtmlMatchesUrl(root: HTMLElement): boolean {
  if (!document.documentElement.hasAttribute('data-prerendered') || !root.hasChildNodes()) return false;
  const canonical = document.querySelector('link[rel="canonical"]')?.getAttribute('href');
  if (!canonical) return false;
  return new URL(canonical, window.location.origin).pathname === window.location.pathname;
}

/**
 * Creates a hidden sibling of the prerendered container for the app to render into, and
 * swaps it in (taking over the container's id) once the page reports ready.
 */
export function createHandoffContainer(prerendered: HTMLElement): HTMLElement {
  const appContainer = document.createElement('div');
  // Hidden with opacity, not display (the table measures its layout) or visibility (inherited,
  // so `transition: all` rules would keep children hidden for a frame after the swap)
  appContainer.setAttribute(
    'style',
    'position:absolute;top:0;left:0;width:100%;height:100vh;overflow:hidden;opacity:0;pointer-events:none;z-index:-1;'
  );
  appContainer.setAttribute('aria-hidden', 'true');
  appContainer.setAttribute('inert', '');
  prerendered.after(appContainer);
  pending = true;

  const swap = () => {
    if (!pending) return;
    pending = false;
    window.removeEventListener(READY_EVENT, swap);
    const id = prerendered.id;
    const windowScroll = window.scrollY;
    const innerScroll = prerendered.querySelector(SCROLL_CONTAINER)?.scrollTop ?? 0;
    prerendered.remove();
    appContainer.removeAttribute('style');
    appContainer.removeAttribute('aria-hidden');
    appContainer.removeAttribute('inert');
    appContainer.id = id;
    const newScroller = appContainer.querySelector(SCROLL_CONTAINER);
    if (newScroller) newScroller.scrollTop = innerScroll;
    window.scrollTo(0, windowScroll);
  };

  if (ready) swap();
  else window.addEventListener(READY_EVENT, swap);
  window.setTimeout(swap, READY_TIMEOUT_MS);

  return appContainer;
}
